import {
    Alert,
    Container,
    convertFromBoolean,
    convertToBoolean,
    Divider,
    Form,
    FormRadio,
    getPageValidationInfo,
    Header,
    SummaryGroup,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ProfileSupernovaCommercialInsured from './Insured';

const ProfileSupernovaCommercialInsureds = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();

    const [mode, setMode] = useState('additionalInsuredsView');
    const [currentAdditionalInsured, setCurrentAdditionalInsured] = useState(null);
    const [currentHasAdditionalInsureds, setCurrentHasAdditionalInsureds] = useState(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }

        setCurrentHasAdditionalInsureds(props?.policy?.additionalInsuredsInfo?.hasAdditionalInsureds ?? false);
    }, []);

    const handleSave = () => {
        props.doReducePolicyElement({
            code: 'additionalInsuredsInfo.hasAdditionalInsureds',
            value: currentHasAdditionalInsureds,
        });

        if (
            currentHasAdditionalInsureds === true &&
            props.policy?.additionalInsureds &&
            props.policy.additionalInsureds.length === 0
        ) {
            props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.CREATEADDITIONALINSURED' });
        } else if (
            currentHasAdditionalInsureds === false &&
            props.policy?.additionalInsureds &&
            props.policy?.additionalInsureds.length > 0
        ) {
            props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.DELETEADDITIONALINSUREDS' });
        } else {
            props.doUpsertPolicy();
        }
    };

    const getAdditionalInsureds = () => {
        const additionalInsureds = [];

        if (props.policy?.additionalInsureds && props.policy.additionalInsureds.length > 0) {
            props.policy.additionalInsureds.forEach((additionalInsured) => {
                const option = { code: 'additionalInsuredOption', value: additionalInsured.guid };

                option.elements = [];
                option.elements.push({ code: 'Label', value: additionalInsured.label });

                additionalInsureds.push(option);
            });
        }

        return additionalInsureds;
    };

    const handleHasAdditionalInsuredsFormRadio = (value) => {
        const valueAsBollean = convertToBoolean(value.code);
        setCurrentHasAdditionalInsureds(valueAsBollean);

        props.doReducePolicyElement({
            code: 'additionalInsuredsInfo.hasAdditionalInsureds',
            value: valueAsBollean,
        });
    };

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);
    const additionalInsureds = getAdditionalInsureds();

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            {mode === 'additionalInsuredsView' && (
                <React.Fragment>
                    <Form
                        pageCode={props.pageCode}
                        sectionCode='additionalInsuredsInfo'
                        saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                        reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                        tooltipInfo={props.tooltipInfo}
                        validationInfo={props.validationInfo}
                        header={<FormattedMessage id='Profile_SupernovaCommercial_Insureds.additionalInsuredsInfo' />}
                        label={<FormattedMessage id='Profile_SupernovaCommercial_Insureds.details' />}
                        allowEdit={props.allowEdit}
                        saveEvent={() => handleSave()}
                        cancelEvent={() => props.doGetPolicy()}
                    >
                        <FormRadio
                            fieldCode='hasAdditionalInsureds'
                            label={
                                <FormattedMessage id='Profile_SupernovaCommercial_Insureds.requireAdditionalInsured' />
                            }
                            value={
                                props.policy.additionalInsuredsInfo?.hasAdditionalInsureds !== undefined && {
                                    code: convertFromBoolean(props.policy.additionalInsuredsInfo.hasAdditionalInsureds),
                                }
                            }
                            references={props.reference?.references.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) => handleHasAdditionalInsuredsFormRadio(value)}
                            allowEdit
                        />
                    </Form>
                    <Divider top={sizes.xl} />
                    <Header
                        size={sizes.lg}
                        textAlign={alignments.left}
                        text={<FormattedMessage id='Profile_SupernovaCommercial_Insureds.additionalInsureds' />}
                    />
                    <Container top={sizes.xl} bottom={sizes.xl}>
                        <SummaryGroup
                            allowAdd={
                                props.allowEdit &&
                                props.policy.additionalInsuredsInfo?.hasAdditionalInsureds &&
                                (props.policy.additionalInsureds === undefined || additionalInsureds.length < 10)
                            }
                            addEvent={() => {
                                props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.CREATEADDITIONALINSURED' });
                            }}
                            allowEdit
                            editEvent={(insured) => {
                                setCurrentAdditionalInsured(insured);
                                setMode('insuredView');
                            }}
                            allowDelete={props.allowEdit && additionalInsureds.length > 0}
                            deleteEvent={(insured) => {
                                props.doReduceParameter({ code: 'AdditionalInsuredGuid', value: insured });
                                props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.DELETEADDITIONALINSURED' });
                            }}
                            entities={additionalInsureds}
                            icon='person'
                        />
                    </Container>
                </React.Fragment>
            )}
            {mode === 'insuredView' && (
                <ProfileSupernovaCommercialInsured
                    rowId={currentAdditionalInsured}
                    pageCode={props.pageCode}
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    additionalInsured={props.policy.additionalInsureds.find((x) => x.guid === currentAdditionalInsured)}
                    dotProp={`additionalInsureds.${props.policy.additionalInsureds.findIndex((x) => x.guid === currentAdditionalInsured)}.`}
                    backEvent={() => {
                        setMode('additionalInsuredsView');
                        setCurrentAdditionalInsured(null);
                    }}
                    doUpsertPolicy={props.doUpsertPolicy}
                    doGetPolicy={props.doGetPolicy}
                    doReduceParameter={props.doReduceParameter}
                    doReducePolicyElement={props.doReducePolicyElement}
                    doExecutePolicyAction={props.doExecutePolicyAction}
                    reference={props.reference}
                    context={props.context}
                    allowEdit={props.allowEdit}
                />
            )}
        </React.Fragment>
    );
};

export default ProfileSupernovaCommercialInsureds;
